import { z } from "zod";

// Single-tenant CRM (per plan §2 Hard Rules): only owner|admin, no company_id.
export const UserRoleSchema = z.enum(["owner", "admin"]);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable().optional(),
  role: UserRoleSchema,
});
export type User = z.infer<typeof UserSchema>;

// The API's TokenResponse is flat (api/app/schemas/auth.py); callers want a
// User, so the parse folds user_id/email/role into one.
export const LoginResponseSchema = z
  .object({
    access_token: z.string(),
    token_type: z.string().optional(),
    user_id: z.string(),
    email: z.string(),
    role: UserRoleSchema,
  })
  .transform((d) => ({
    access_token: d.access_token,
    user: { id: d.user_id, email: d.email, role: d.role } satisfies User,
  }));
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const LoginRequestSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

// Opt-in 2FA: POST /v2/auth/login returns THIS instead of LoginResponse when
// the account has 2FA enabled — no token yet, just a short-lived challenge
// to hand to POST /v2/auth/2fa/login alongside the 6-digit code.
export const LoginChallengeResponseSchema = z.object({
  totp_required: z.literal(true),
  challenge: z.string(),
});
export type LoginChallengeResponse = z.infer<typeof LoginChallengeResponseSchema>;

export const TwoFactorLoginRequestSchema = z.object({
  challenge: z.string(),
  code: z.string().min(1, "Enter the 6-digit code"),
});
export type TwoFactorLoginRequest = z.infer<typeof TwoFactorLoginRequestSchema>;
